/**
 * The CLARITY Assessment configuration and scoring.
 *
 * Separated from the modal that renders it so the copy can be proofread in one
 * place, and so a future shareable results page can import the same
 * definitions rather than restating them. Everything here is pure data or a
 * pure function: no React, no side effects.
 */

/* ============================================================
   The CLARITY Assessment configuration
   Four questions, no account. Step 3 is the engine: each answer
   maps to the regulatory domains it puts in play, and the breadth
   of that unique domain set is the whole output. Nothing here is
   scored on sentiment or self-reported confidence.
   ============================================================ */

export const ROLES = [
  { id: "gc", label: "General Counsel or in-house counsel" },
  { id: "private", label: "Private practice attorney" },
  { id: "litigator", label: "Litigator or disputes practice" },
  { id: "grc", label: "GRC or compliance lead" },
  { id: "founder", label: "Founder or executive" },
  { id: "policy", label: "Policy, government, or academic" },
  { id: "cfo", label: "CFO, treasury, or finance leader" },
  { id: "product", label: "Product, payments, or engineering lead" },
];

export const MATTERS = [
  { id: "litigation", label: "Litigation or disputes involving digital assets", hint: "Payment tracking, tracing, discovery, valuation" },
  { id: "family", label: "Family law or estate matter with crypto assets", hint: "Divorce, probate, disclosure, division" },
  { id: "bankruptcy", label: "Bankruptcy or insolvency with digital assets", hint: "Estate property, clawbacks, priority" },
  { id: "nonprofit", label: "Nonprofit or treasury accepting crypto", hint: "Donations, receipting, conversion" },
  { id: "stablecoin", label: "Stablecoin or payments activity", hint: "Issuance, integration, or acceptance" },
  { id: "token", label: "Vetting or launching a token project", hint: "How the token is classified and offered" },
  { id: "custody", label: "Fund, custody, or exchange operations", hint: "Registration surface, licensing, controls" },
  { id: "multiple", label: "Advising across multiple client matters", hint: "A recurring stream of digital-asset questions" },
];

/* The twelve regulatory domains, in the order they are reported back. A fixed
   order means two visitors who select the same answers in a different
   sequence read the same result. */
export const DOMAIN_ORDER = [
  "Securities",
  "AML-KYC-Sanctions",
  "Custody & Safekeeping",
  "Tax",
  "Consumer Protection",
  "Market Integrity",
  "Data & Privacy",
  "Cybersecurity",
  "Commodities & Derivatives",
  "Banking & Payments",
  "Corporate & Governance",
  "IP & Smart Contracts",
];

export const MAX_DOMAINS = DOMAIN_ORDER.length;

/* The question each flagged domain puts to the visitor. Not an answer: the
   point is to show what a full issue-mapping run would have to resolve. */
export const DOMAIN_QUESTIONS = {
  "Securities": "Could anyone view what's offered, or the returns on it, as an investment in someone else's efforts?",
  "AML-KYC-Sanctions": "Who verifies identities and screens counterparties, and is that documented?",
  "Custody & Safekeeping": "Whose name are the assets held in, and who controls the keys?",
  "Tax": "Has each transfer, sale, or reward been identified as a potential taxable event, with cost basis tracked?",
  "Consumer Protection": "What disclosures do everyday users receive, and do they match what actually happens?",
  "Market Integrity": "Who sets prices or provides liquidity, and is any of them affiliated with the issuer?",
  "Data & Privacy": "What personal data is collected, where does it live, and which countries' rules follow it?",
  "Cybersecurity": "If keys or systems were compromised tomorrow, who would know first, and what happens next?",
  "Commodities & Derivatives": "Does anything here involve leverage, futures, swaps, or synthetic exposure?",
  "Banking & Payments": "Which licensed institution touches the fiat side, and does your activity fit its permissions?",
  "Corporate & Governance": "Who has authority to decide here, and would the record hold up if a board, court, or regulator asked?",
  "IP & Smart Contracts": "Who owns and can change the code, and has it been independently reviewed?",
};

/* Step 3. The domain mapping is backend logic and is never shown as a list of
   domains next to the option; the visitor sees only the facts of their own
   situation, then the domains those facts put in play. */
export const UNSURE_ID = "unsure";

export const SITUATIONS = [
  { id: "custody_others", label: "Someone holds digital assets on behalf of others, even briefly",
    domains: ["Custody & Safekeeping", "AML-KYC-Sanctions", "Cybersecurity"] },
  { id: "tokens_offered", label: "Tokens are being offered, sold, or distributed to others",
    domains: ["Securities", "Commodities & Derivatives", "Tax"] },
  { id: "fiat_movement", label: "Money moves between crypto and dollars (or another currency)",
    domains: ["Banking & Payments", "AML-KYC-Sanctions", "Tax"] },
  { id: "yield_rewards", label: "Users or counterparties earn yield, rewards, or staking returns",
    domains: ["Securities", "Tax"] },
  { id: "exchange_liquidity", label: "Assets trade on an exchange, or someone provides liquidity",
    domains: ["Market Integrity", "Securities", "Commodities & Derivatives"] },
  { id: "smart_contracts", label: "Smart contracts, a protocol, or a DAO are part of the setup",
    domains: ["IP & Smart Contracts", "Corporate & Governance", "Cybersecurity"] },
  { id: "multi_country", label: "More than one country is involved",
    domains: ["AML-KYC-Sanctions", "Data & Privacy", "Tax"] },
  { id: "consumers", label: "Everyday consumers are involved, not just institutions",
    domains: ["Consumer Protection", "Data & Privacy"] },
  { id: "explain_authority", label: "I'll need to explain this to a court, board, or regulator",
    domains: ["Corporate & Governance"] },
  { id: UNSURE_ID, label: "I'm not sure which of these apply", domains: [] },
];

export const TIMELINES = [
  { id: "live", label: "Active matter or live product now" },
  { id: "soon", label: "Expected within the next 3 months" },
  { id: "exploring", label: "Exploratory, building competence ahead of demand" },
];

/* The Clarity Index. Breadth of surface, not readiness: a higher level means
   MORE domains in play. Mirrored verbatim in backend/clarity_index.py, which
   supplies the same copy to the result email. Keep the two in sync. */
export const CLARITY_INDEX = {
  1: {
    name: "Contained surface",
    text: "Your responses describe a contained regulatory surface, with a small number of domains in play. Contained is not the same as simple. A full Issue-Mapping Memorandum confirms whether anything sits outside what these answers show.",
  },
  2: {
    name: "Broad surface",
    text: "Your responses describe a broad regulatory surface, with several domains in play at once. Where domains overlap, issues can fall between them. A targeted Issue-Mapping Memorandum maps each one and prioritizes what to address first.",
  },
  3: {
    name: "Complex surface",
    text: "Your responses describe a complex regulatory surface, with many domains in play at once. At this breadth, prioritization matters as much as identification. A full Issue-Mapping Memorandum ranks what needs attention first.",
  },
};

export const OPEN_FACT_PATTERN = {
  name: "Fact pattern open",
  text: "Your answers leave the fact pattern open. That's the first finding: before anyone can map the risk, someone has to pin down what's actually happening. A guided intake does exactly that.",
};

export const OPEN_LOOP =
  "This map is built from four answers. Which regulators are in play depends on your jurisdictions and facts, which this assessment doesn't ask. A full Issue-Mapping Memorandum does.";

/* The retired Step 3 vocabulary. Kept only so the admin Assessment Insights
   card can still label the historical rows recorded against it. */
export const CONCERNS = [
  { id: "blindspot", label: "Unseen regulatory exposure, the issue I have not spotted yet" },
  { id: "regulators", label: "Determining which regulators are actually in play" },
  { id: "secvcom", label: "Security vs commodity uncertainty while the CLARITY Act remains pending" },
  { id: "genius", label: "GENIUS Act obligations now attaching to stablecoin activity" },
  { id: "tracing", label: "Tracing, documenting, or valuing digital assets for a proceeding" },
  { id: "plain", label: "Explaining the analysis to a board, court, or client in plain language" },
  { id: "churn", label: "Keeping the analysis current as frameworks shift" },
  { id: "timeline", label: "Timeline pressure, the matter or launch will not wait" },
];

/* Seats that carry budget authority and board exposure. With an active matter
   they are the highest-value inbound signal in the product, so they skip the
   self-serve tiers and go to a briefing request. */
const BRIEFING_SEATS = ["gc", "grc", "founder", "cfo"];

export function computeTriage({ role, matter, situations = [], timeline }) {
  const open = situations.includes(UNSURE_ID);
  const picked = open
    ? []
    : SITUATIONS.filter((s) => s.id !== UNSURE_ID && situations.includes(s.id));

  // domain -> the answers that put it in play. De-duplicated by construction:
  // a domain triggered by three answers is still one domain, listed once with
  // all three reasons.
  const triggered = new Map();
  for (const s of picked) {
    for (const d of s.domains) {
      if (!triggered.has(d)) triggered.set(d, []);
      triggered.get(d).push(s.label);
    }
  }
  const domains = DOMAIN_ORDER.filter((d) => triggered.has(d)).map((d) => ({
    name: d,
    because: triggered.get(d),
    question: DOMAIN_QUESTIONS[d],
  }));

  const count = domains.length;
  // Breadth level: 1-3 domains, 4-6, 7-12. An open fact pattern carries the
  // lowest level so the outbound payload keeps its shape, but the result
  // screen shows no level name for it.
  const stage = count >= 7 ? 3 : count >= 4 ? 2 : 1;
  // The only number the acquisition webhook carries: domain count scaled to a
  // 0-100 integer, so 12 domains is 100 and 0 is 0.
  const score = Math.round((count * 100) / MAX_DOMAINS);

  // First match wins. The order is the routing policy: the briefing request
  // outranks every self-serve tier, and a recurring advisory stream outranks
  // the single-scenario product.
  const rec =
    BRIEFING_SEATS.includes(role) && timeline === "live"
      ? "strategy"
      : matter === "multiple"
        ? "explore"
        : role === "policy" || timeline === "exploring"
          ? "webinar"
          : "solo";

  return { count, domains, stage, score, open, rec, band: (open ? OPEN_FACT_PATTERN : CLARITY_INDEX[stage]).name };
}

/* Where each routing outcome sends the visitor. `strategy` has no `to`: its
   next step is the briefing form rendered inline on the result screen.
   `webinar` takes its headline and body from the live session schedule, so it
   names the actual next session rather than the series in the abstract. */
export const RECOMMENDATIONS = {
  solo: {
    label: "Clarity SOLO™ · $297 once",
    headline: "Your single scenario, mapped and prioritized, immediately.",
    body: "Your responses describe a discrete matter. Run it through the full Issue-Mapping ADVANTAGE and receive a memorandum-grade PDF, including the plain-language Executive Brief. Upgrade within 7 days of first use and 100% of the $297 applies to your first EXPLORE payment.",
    cta: "Map your situation with Clarity SOLO · $297 →",
    to: "/register?plan=single",
  },
  explore: {
    label: "Clarity EXPLORE™ · $497/mo or $4,970/yr",
    headline: "Deeper, ongoing coverage across every matter that reaches your desk.",
    body: "Your responses describe a broad, active issue surface. Unlimited scenario runs keep every matter mapped as frameworks shift. Subscribe at the 2026 rate and keep it for as long as you stay subscribed; 2027 pricing is $1,500/mo.",
    cta: "Start EXPLORE · $497/mo →",
    to: "/register?plan=monthly",
  },
  enterprise: {
    label: "Clarity CORE™ / ENCORE™",
    headline: "Enterprise-level procurement and GRC program needs.",
    body: "Your responses describe an institutional mandate: portfolio-wide coverage, governance reporting, and procurement requirements. The CORE and ENCORE tiers are built for that remit, with multi-seat licensing and enterprise terms available.",
    cta: "Review the enterprise tiers →",
    to: "/pricing#tier-core",
  },
  strategy: {
    label: "Strategic Advantage Briefing",
    headline: "Move from mapped issues to a tailored discussion of priorities, decisions, and next steps.",
    body: "Custom-tailored by Advantage Evans experts and quoted based on the complexity of the matter. No standard fee applies.",
  },
  webinar: {
    label: "The CLARITY Briefing · Free",
    cta: "Join the Thursday Clarity Briefing →",
    to: "/webinar",
  },
};

/* The self-serve tiers offered as secondary links. Every tier except the one
   the visitor was routed to: the briefing route counts as the enterprise
   tier, so it keeps SOLO and EXPLORE. */
export const TIERS = ["solo", "explore", "enterprise"];
export const TIER_ROUTED = { solo: "solo", explore: "explore", strategy: "enterprise", webinar: null };
export const TIER_SHORT = { solo: "Clarity SOLO™", explore: "Clarity EXPLORE™", enterprise: "Clarity CORE™ / ENCORE™" };
